import type { ScopeKey } from '@unipile/types';
import { CheckCircle2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../shadcn/Button';
import { Spinner } from '../shadcn/Spinner';

type Props = {
  /**
   * Generic scopes to display (optional - can be empty if only using children)
   */
  scopes?: ScopeKey[];
  brandName?: string;
  onAccept: () => void;
  onCancel: () => void;
  isPending?: boolean;
  /**
   * Provider-specific consent content to display alongside or instead of scopes.
   * This allows combining generic scopes with provider-specific consent in one screen.
   */
  children?: ReactNode;
};

/**
 * A consent screen that displays the requested scopes and/or provider-specific content.
 * Should be used inside a ProviderProvider context.
 *
 * Each provider can decide when to show this screen in their flow:
 * - WhatsApp: BEFORE the QR code (since scanning = immediate connection)
 * - LinkedIn: AFTER credentials, BEFORE submission (can include products consent)
 * - etc.
 *
 * @example
 * // With only scopes
 * <ScopesConsentScreen scopes={scopes} onAccept={...} onCancel={...} />
 *
 * @example
 * // With scopes and provider-specific content
 * <ScopesConsentScreen scopes={scopes} onAccept={...} onCancel={...}>
 *   <ProductsConsentContent products={products} />
 * </ScopesConsentScreen>
 *
 * @example
 * // With only provider-specific content (no generic scopes)
 * <ScopesConsentScreen onAccept={...} onCancel={...}>
 *   <CustomConsentContent />
 * </ScopesConsentScreen>
 */
export function ScopesConsentScreen({ scopes = [], onAccept, onCancel, isPending, children }: Props) {
  const { t } = useTranslation();
  const hasScopes = scopes.length > 0;
  const hasContent = hasScopes || children;

  if (!hasContent) {
    // No content to show, skip
    return null;
  }

  return (
    <div className="space-y-4">
      <ScopesConsentContent scopes={scopes} />

      {children}

      <div className="flex flex-col gap-3 pt-4">
        <Button onClick={onAccept} className="w-full" disabled={isPending}>
          {isPending && <Spinner />}
          {t('ConsentScreen.accept')}
        </Button>
        <Button onClick={onCancel} variant="outline" className="w-full" disabled={isPending}>
          {t('ConsentScreen.cancel')}
        </Button>
      </div>
    </div>
  );
}

export function ScopesConsentContent({ scopes }: { scopes: ScopeKey[] }) {
  const { t } = useTranslation();

  if (scopes.length === 0) return null;

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-left text-sm">{t('ConsentScreen.description')}</p>
      <div className="bg-muted/50 space-y-3 rounded-lg p-4">
        {scopes.toSorted().map((scope) => (
          <ScopeItem key={scope} scope={scope} />
        ))}
      </div>
    </div>
  );
}

function ScopeItem({ scope }: { scope: ScopeKey }) {
  const { t } = useTranslation();
  const scopeKey = scope.replace(':', '_');
  const title = t(`Scope.${scopeKey}.title`);
  const description = t(`Scope.${scopeKey}.description`);

  return (
    <div className="flex items-start gap-3">
      <CheckCircle2Icon className="text-primary mt-0.5 h-5 w-5 flex-shrink-0" />
      <div className="space-y-0.5">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-muted-foreground text-xs">{description}</div>
      </div>
    </div>
  );
}
